import { createBrowserClient } from '@supabase/ssr';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

// Environment variables for Supabase connection
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env file and ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set.'
  );
}

// Validate Supabase URL format
try {
  const url = new URL(supabaseUrl);
  if (!url.hostname.includes('supabase')) {
    console.warn(
      '[Supabase] URL does not appear to be a Supabase URL:',
      supabaseUrl
    );
  }
} catch (error: unknown) {
  const message = error instanceof Error ? error.message : 'Unknown error';
  throw new Error(
    `Invalid VITE_SUPABASE_URL format: ${supabaseUrl}. Must be a valid URL. ${message}`
  );
}

// The session lives in cookies (chunked by @supabase/ssr) instead of
// localStorage so it can be shared with the standalone V2 apps
// (e.g. workflow.tifpaconsulting.com). On *.tifpaconsulting.com the cookie
// is scoped to .tifpaconsulting.com so the portal and the V2 apps see the
// same session. Anywhere else (localhost, *.pages.dev previews) the cookie
// stays host-scoped — browsers drop cookies whose Domain doesn't match the
// host.
const onTifpaDomain = window.location.hostname.endsWith('tifpaconsulting.com');

// Explicit annotation: @supabase/ssr 0.5.x instantiates SupabaseClient with
// the pre-2.5x generic order, landing the schema object in the SchemaName slot
// and collapsing .insert()/.update()/.rpc() typing to `never` project-wide.
// Pinning <Database, 'public'> restores schema resolution without touching the
// runtime client. (Upgrading @supabase/ssr would also fix it, but the
// cookie-auth setup is shared with the V2 apps and not worth the risk.)
export const supabase: SupabaseClient<Database, 'public'> =
  createBrowserClient<Database>(
    supabaseUrl,
    supabaseAnonKey,
    {
      cookieOptions: {
        ...(onTifpaDomain ? { domain: '.tifpaconsulting.com' } : {}),
        path: '/',
        sameSite: 'lax',
        secure: window.location.protocol === 'https:',
        maxAge: 60 * 60 * 24 * 60, // 60 days
      },
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        // PKCE flow prevents authorization code interception
        flowType: 'pkce',
      },
      realtime: {
        params: {
          eventsPerSecond: 100, // Max events per second
        },
        heartbeatIntervalMs: 30000, // 30 seconds
        reconnectAfterMs: (tries: number) => {
          // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
          return Math.min(1000 * Math.pow(2, tries), 30000);
        },
      },
    }
    // The cast bridges ssr's old-generic return type to the annotation above;
    // both describe the same runtime client.
  ) as unknown as SupabaseClient<Database, 'public'>;

// One-time migration from the old localStorage-backed session
// ('tifpa-auth-token', and the mobile variant) to cookie storage, so
// employees are not logged out by the storage switch. Runs before the app
// renders anything auth-dependent; AuthContext picks the session up via
// getSession()/onAuthStateChange as usual.
const LEGACY_STORAGE_KEYS = ['tifpa-auth-token', 'supabase.auth.token.mobile'];

async function migrateLegacySession(): Promise<void> {
  try {
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      LEGACY_STORAGE_KEYS.forEach(key => window.localStorage.removeItem(key));
      return;
    }
    for (const key of LEGACY_STORAGE_KEYS) {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      window.localStorage.removeItem(key);
      const parsed = JSON.parse(raw) as {
        access_token?: string;
        refresh_token?: string;
      };
      if (parsed?.access_token && parsed?.refresh_token) {
        const { error } = await supabase.auth.setSession({
          access_token: parsed.access_token,
          refresh_token: parsed.refresh_token,
        });
        if (!error) return;
      }
    }
  } catch {
    // Migration is best-effort; worst case the user signs in again.
  }
}

export const legacySessionMigration = migrateLegacySession();

// Export types for convenience
export type { Database } from './database.types';
